// Exports: default
import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import TaskGroupDetailComponent from "./TaskGroupDetailComponent";
import TaskDetailComponent from "./TaskDetailComponent";
import SubtaskDetailComponent from "./SubTaskDetailComponent";
import KanbanView from "./KanbanView";
import MainTableView from "./MainTableView";
import CustomGantt from "./CustomGantt";
import TaskDashboardView from "./TaskDashboardView";
import { Tabs, TabsList, TabsTrigger } from "components/ui-custom/Tabs";
import useScrollAnchor from "hooks/useScrollAnchor";
import useTaskGroups from "hooks/useTaskGroups";
import { getAvatarColor, getInitials } from "utils/tasks/taskHelpers";

const TaskHome = () => {
  const SIDEBAR_TRANSITION_MS = 300;
  const siteId = Number(localStorage.getItem("site"));
  const token = localStorage.getItem("access");

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    }),
    [token],
  );

  const {
    taskGroups,
    refreshing,
    statusOptions,
    statePersistentRefresh,
    fetchTasksForGroup,
    fetchSubtasksForTask,
    onToggleGroup,
    onToggleTask,
    collapseAllTaskGroups,
    expandAllTaskGroups,
    reorderTasksInGroup,
    reorderSubtasksInTask,
    addTaskGroup,
    handleAddTask,
    handleAddSubtask,
    deleteSubtask,
    onGroupEditStart,
    onGroupEditCancel,
    onGroupEditSubmit,
    editingGroupId,
    editingGroupName,
    onTaskEditStart,
    onTaskEditCancel,
    onTaskEditSubmit,
    editingTaskId,
    editingTaskName,
    onSubEditStart,
    onSubEditCancel,
    onSubEditSubmit,
    editingSubId,
    editingSubName,
    onTaskStatusChange,
    onTaskPriorityChange,
    onToggleSubComplete,
    handleTaskDateChange,
    handleSubtaskDateChange,
    mergeGroupAllocatedMembers,
    openStatusDropdown,
    setOpenStatusDropdown,
    openPriorityDropdown,
    setOpenPriorityDropdown,
    getStatusProportions,
    updateGroupNameLocally,
  } = useTaskGroups(siteId, authHeaders);

  const [activeView, setActiveView] = useState("Main Table");
  const [selectedTaskGroup, setSelectedTaskGroup] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedSubtask, setSelectedSubtask] = useState(null);
  const [showAddTaskGroupInput, setShowAddTaskGroupInput] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(null);
  const [showAddSubtaskModal, setShowAddSubtaskModal] = useState(null);
  const [isTaskGroupSidebarVisible, setIsTaskGroupSidebarVisible] =
    useState(false);
  const [isTaskSidebarVisible, setIsTaskSidebarVisible] = useState(false);
  const [isSubtaskSidebarVisible, setIsSubtaskSidebarVisible] = useState(false);
  const taskGroupSidebarCloseTimeoutRef = useRef(null);
  const taskSidebarCloseTimeoutRef = useRef(null);
  const subtaskSidebarCloseTimeoutRef = useRef(null);

  const { scrollContainerRef, captureScrollAnchor, preserveScroll } =
    useScrollAnchor([
      taskGroups,
      activeView,
      selectedTaskGroup,
      selectedTask,
      selectedSubtask,
      showAddTaskGroupInput,
      showAddTaskModal,
      showAddSubtaskModal,
      editingGroupId,
      editingTaskId,
      editingSubId,
      openStatusDropdown,
      openPriorityDropdown,
    ]);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const deepLinkTaskId = searchParams.get("taskId");
    const deepLinkSubtaskId = searchParams.get("subtaskId");

    if (deepLinkSubtaskId && deepLinkTaskId) {
      setSelectedSubtask({
        id: Number(deepLinkSubtaskId),
        task_id: Number(deepLinkTaskId),
      });
      searchParams.delete("subtaskId");
      searchParams.delete("taskId");
      setSearchParams(searchParams, { replace: true });
    } else if (deepLinkTaskId) {
      setSelectedTask({ id: Number(deepLinkTaskId) });
      searchParams.delete("taskId");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const clearTaskGroupSidebarCloseTimeout = useCallback(() => {
    if (taskGroupSidebarCloseTimeoutRef.current) {
      window.clearTimeout(taskGroupSidebarCloseTimeoutRef.current);
      taskGroupSidebarCloseTimeoutRef.current = null;
    }
  }, []);

  const clearTaskSidebarCloseTimeout = useCallback(() => {
    if (taskSidebarCloseTimeoutRef.current) {
      window.clearTimeout(taskSidebarCloseTimeoutRef.current);
      taskSidebarCloseTimeoutRef.current = null;
    }
  }, []);

  const clearSubtaskSidebarCloseTimeout = useCallback(() => {
    if (subtaskSidebarCloseTimeoutRef.current) {
      window.clearTimeout(subtaskSidebarCloseTimeoutRef.current);
      subtaskSidebarCloseTimeoutRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      clearTaskGroupSidebarCloseTimeout();
      clearTaskSidebarCloseTimeout();
      clearSubtaskSidebarCloseTimeout();
    },
    [
      clearTaskGroupSidebarCloseTimeout,
      clearTaskSidebarCloseTimeout,
      clearSubtaskSidebarCloseTimeout,
    ],
  );

  useEffect(() => {
    if (!selectedTaskGroup) {
      setIsTaskGroupSidebarVisible(false);
      return;
    }

    clearTaskGroupSidebarCloseTimeout();
    const frame = window.requestAnimationFrame(() => {
      setIsTaskGroupSidebarVisible(true);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [clearTaskGroupSidebarCloseTimeout, selectedTaskGroup]);

  useEffect(() => {
    if (!selectedTask) {
      setIsTaskSidebarVisible(false);
      return;
    }

    clearTaskSidebarCloseTimeout();
    const frame = window.requestAnimationFrame(() => {
      setIsTaskSidebarVisible(true);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [clearTaskSidebarCloseTimeout, selectedTask]);

  useEffect(() => {
    if (!selectedSubtask) {
      setIsSubtaskSidebarVisible(false);
      return;
    }

    clearSubtaskSidebarCloseTimeout();
    const frame = window.requestAnimationFrame(() => {
      setIsSubtaskSidebarVisible(true);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [clearSubtaskSidebarCloseTimeout, selectedSubtask]);

  const handleRefresh = useCallback(
    () => preserveScroll(() => statePersistentRefresh()),
    [preserveScroll, statePersistentRefresh],
  );

  const handleRefetchTasks = useCallback(
    (groupId) => {
      let actualGroupId = groupId;
      if (!actualGroupId && selectedTask) {
        for (const group of taskGroups) {
          if (
            group.tasks?.some((t) => String(t.id) === String(selectedTask.id))
          ) {
            actualGroupId = group.id;
            break;
          }
        }
      }
      return preserveScroll(
        `[data-scroll-anchor="group-${actualGroupId}"]`,
        () => fetchTasksForGroup(actualGroupId),
      );
    },
    [fetchTasksForGroup, preserveScroll, selectedTask, taskGroups],
  );

  const handleRefetchSubtasks = useCallback(
    (groupId, taskId) => {
      let actualGroupId = groupId;
      if (!actualGroupId) {
        for (const group of taskGroups) {
          if (group.tasks?.some((t) => String(t.id) === String(taskId))) {
            actualGroupId = group.id;
            break;
          }
        }
      }
      return preserveScroll(`[data-scroll-anchor="task-${taskId}"]`, () =>
        fetchSubtasksForTask(actualGroupId, taskId),
      );
    },
    [fetchSubtasksForTask, preserveScroll, taskGroups],
  );

  const handleSetSelectedTaskGroup = useCallback(
    (value) =>
      preserveScroll(() => {
        if (value) {
          clearTaskGroupSidebarCloseTimeout();
        }
        setSelectedTaskGroup(value);
      }),
    [clearTaskGroupSidebarCloseTimeout, preserveScroll],
  );

  const handleSetSelectedTask = useCallback(
    (value) =>
      preserveScroll(() => {
        if (value) {
          clearTaskSidebarCloseTimeout();
        }
        setSelectedTask(value);
      }),
    [clearTaskSidebarCloseTimeout, preserveScroll],
  );

  const handleSetSelectedSubtask = useCallback(
    (value) =>
      preserveScroll(() => {
        if (value) {
          clearSubtaskSidebarCloseTimeout();
        }
        setSelectedSubtask(value);
      }),
    [clearSubtaskSidebarCloseTimeout, preserveScroll],
  );

  const closeTaskSidebar = useCallback(() => {
    if (!selectedTask) return;

    setIsTaskSidebarVisible(false);
    clearTaskSidebarCloseTimeout();
    taskSidebarCloseTimeoutRef.current = window.setTimeout(() => {
      setSelectedTask(null);
      taskSidebarCloseTimeoutRef.current = null;
    }, SIDEBAR_TRANSITION_MS);
  }, [
    SIDEBAR_TRANSITION_MS,
    clearTaskSidebarCloseTimeout,
    selectedTask,
  ]);

  const closeTaskGroupSidebar = useCallback(() => {
    if (!selectedTaskGroup) return;

    setIsTaskGroupSidebarVisible(false);
    clearTaskGroupSidebarCloseTimeout();
    taskGroupSidebarCloseTimeoutRef.current = window.setTimeout(() => {
      setSelectedTaskGroup(null);
      taskGroupSidebarCloseTimeoutRef.current = null;
    }, SIDEBAR_TRANSITION_MS);
  }, [
    SIDEBAR_TRANSITION_MS,
    clearTaskGroupSidebarCloseTimeout,
    selectedTaskGroup,
  ]);

  const closeSubtaskSidebar = useCallback(() => {
    if (!selectedSubtask) return;

    setIsSubtaskSidebarVisible(false);
    clearSubtaskSidebarCloseTimeout();
    subtaskSidebarCloseTimeoutRef.current = window.setTimeout(() => {
      setSelectedSubtask(null);
      subtaskSidebarCloseTimeoutRef.current = null;
    }, SIDEBAR_TRANSITION_MS);
  }, [
    SIDEBAR_TRANSITION_MS,
    clearSubtaskSidebarCloseTimeout,
    selectedSubtask,
  ]);

  const handleDashboardTaskOpen = useCallback(
    (task) =>
      preserveScroll(() => {
        setSelectedTask(task);
        setSelectedTaskGroup(null);
        setSelectedSubtask(null);
      }),
    [preserveScroll],
  );

  const handleDashboardSubtaskOpen = useCallback(
    (subtask) =>
      preserveScroll(() => {
        setSelectedSubtask(subtask);
        setSelectedTaskGroup(null);
        setSelectedTask(null);
      }),
    [preserveScroll],
  );

  const handleAddTaskGroupClick = useCallback(
    () => preserveScroll(() => setShowAddTaskGroupInput(true)),
    [preserveScroll],
  );

  const handleAddTaskClick = useCallback(
    (groupId) =>
      preserveScroll(`[data-scroll-anchor="group-${groupId}"]`, () => {
        setShowAddTaskModal(groupId);
        setShowAddSubtaskModal(null);
      }),
    [preserveScroll],
  );

  const handleAddSubtaskClick = useCallback(
    (_groupId, taskId) =>
      preserveScroll(`[data-scroll-anchor="task-${taskId}"]`, () => {
        setShowAddSubtaskModal(taskId);
        setShowAddTaskModal(null);
      }),
    [preserveScroll],
  );

  const handleAssignApprovers = useCallback(async () => {}, []);
  const handleAddTaskUpdate = useCallback(async () => {}, []);

  const handleAddTaskWithRefetch = useCallback(
    async (taskData) => {
      const result = await handleAddTask(taskData);
      if (result && taskData?.tgId) {
        await handleRefetchTasks(taskData.tgId);
      }
      return result;
    },
    [handleAddTask, handleRefetchTasks],
  );

  const handleAddSubtaskWithRefetch = useCallback(
    async (taskData) => {
      const result = await handleAddSubtask(taskData);
      const taskId = taskData?.taskId ?? taskData?.task_id;
      const groupId = taskData?.tgId;
      if (result && taskId && groupId) {
        await handleRefetchSubtasks(groupId, taskId);
      }
      return result;
    },
    [handleAddSubtask, handleRefetchSubtasks],
  );

  const handleCollapseAll = useCallback(
    () => preserveScroll(() => collapseAllTaskGroups()),
    [collapseAllTaskGroups, preserveScroll],
  );

  const handleExpandAll = useCallback(
    () => preserveScroll(() => expandAllTaskGroups()),
    [expandAllTaskGroups, preserveScroll],
  );

  return (
    <div className="">
      <div className="flex items-center justify-between px-4 py-3">
        <Tabs value={activeView} onValueChange={setActiveView}>
          <TabsList className="h-9 p-1">
            <TabsTrigger value="Dashboard" className="h-7 text-sm px-4 gap-1.5">
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="Main Table" className="h-7 text-sm px-4 gap-1.5">
              Main Table
            </TabsTrigger>
            <TabsTrigger value="Timeline" className="h-7 text-sm px-4 gap-1.5">
              Timeline
            </TabsTrigger>
            <TabsTrigger value="Kanban" className="h-7 text-sm px-4 gap-1.5">
              Kanban
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div
        ref={scrollContainerRef}
        onMouseDownCapture={captureScrollAnchor}
        onTouchStartCapture={captureScrollAnchor}
        className="flex-1 h-[82vh] overflow-y-scroll"
        style={{
          scrollbarWidth: "none",
        }}
      >
        {activeView === "Dashboard" && (
          <TaskDashboardView
            onOpenTask={handleDashboardTaskOpen}
            onOpenSubtask={handleDashboardSubtaskOpen}
          />
        )}

        {activeView === "Timeline" && <CustomGantt />}

        {activeView === "Kanban" && (
          <KanbanView
            taskGroups={taskGroups}
            refetchTasks={handleRefetchTasks}
            onTaskStatusChange={onTaskStatusChange}
            getInitials={getInitials}
            getAvatarColor={getAvatarColor}
          />
        )}

        {activeView === "Main Table" && (
          <MainTableView
            taskGroups={taskGroups}
            hasGroups={taskGroups.length > 0}
            refetchTasks={handleRefetchTasks}
            refetchSubtasks={handleRefetchSubtasks}
            mergeGroupAllocatedMembers={mergeGroupAllocatedMembers}
            onAddTaskGroupClick={handleAddTaskGroupClick}
            onAddTaskClick={handleAddTaskClick}
            handleAddTask={handleAddTaskWithRefetch}
            showAddTaskModal={showAddTaskModal}
            setShowAddTaskModal={setShowAddTaskModal}
            onToggleGroup={onToggleGroup}
            onToggleTask={onToggleTask}
            onCollapseAll={handleCollapseAll}
            onExpandAll={handleExpandAll}
            onTaskReorder={reorderTasksInGroup}
            onSubtaskReorder={reorderSubtasksInTask}
            refreshing={refreshing}
            onRefresh={handleRefresh}
            onAddSubtaskClick={handleAddSubtaskClick}
            showAddSubtaskModal={showAddSubtaskModal}
            setShowAddSubtaskModal={setShowAddSubtaskModal}
            handleAddSubtask={handleAddSubtaskWithRefetch}
            onToggleSubComplete={onToggleSubComplete}
            onTaskStatusChange={onTaskStatusChange}
            openStatusDropdown={openStatusDropdown}
            setOpenStatusDropdown={setOpenStatusDropdown}
            statusOptions={statusOptions}
            getInitials={getInitials}
            onTaskPriorityChange={onTaskPriorityChange}
            openPriorityDropdown={openPriorityDropdown}
            setOpenPriorityDropdown={setOpenPriorityDropdown}
            getAvatarColor={getAvatarColor}
            getStatusProportions={getStatusProportions}
            editingGroupId={editingGroupId}
            editingGroupName={editingGroupName}
            onGroupEditStart={onGroupEditStart}
            onGroupEditCancel={onGroupEditCancel}
            onGroupEditSubmit={onGroupEditSubmit}
            editingTaskId={editingTaskId}
            editingTaskName={editingTaskName}
            onTaskEditStart={onTaskEditStart}
            onTaskEditCancel={onTaskEditCancel}
            onTaskEditSubmit={onTaskEditSubmit}
            editingSubId={editingSubId}
            editingSubName={editingSubName}
            onSubEditStart={onSubEditStart}
            onSubEditCancel={onSubEditCancel}
            onSubEditSubmit={onSubEditSubmit}
            deleteSubtask={deleteSubtask}
            setSelectedTaskGroup={handleSetSelectedTaskGroup}
            selectedTaskGroup={selectedTaskGroup}
            selectedTask={selectedTask}
            selectedSubtask={selectedSubtask}
            setSelectedTask={handleSetSelectedTask}
            setSelectedSubtask={handleSetSelectedSubtask}
            handleTaskDateChange={handleTaskDateChange}
            handleSubtaskDateChange={handleSubtaskDateChange}
            preserveScroll={preserveScroll}
            showAddTaskGroupInput={showAddTaskGroupInput}
            setShowAddTaskGroupInput={setShowAddTaskGroupInput}
            addTaskGroup={addTaskGroup}
          />
        )}
      </div>

      {selectedTaskGroup && (
        <div className="fixed inset-0 z-40">
          <div
            className={`absolute inset-0 bg-black/30 transition-opacity duration-300 ${
              isTaskGroupSidebarVisible ? "opacity-100" : "opacity-0"
            }`}
            onMouseDown={closeTaskGroupSidebar}
          />
          <TaskGroupDetailComponent
            taskGroup={
              taskGroups.find((g) => g.id === selectedTaskGroup.id) ||
              selectedTaskGroup
            }
            onClose={closeTaskGroupSidebar}
            onAssignApprovers={handleAssignApprovers}
            onAddUpdate={handleAddTaskUpdate}
            refetchTaskGroups={handleRefresh}
            onGroupEditStart={onGroupEditStart} // ← add
            onGroupEditSubmit={onGroupEditSubmit}
            updateGroupNameLocally={updateGroupNameLocally}
            isVisible={isTaskGroupSidebarVisible}
          />
        </div>
      )}

      {selectedTask && (
        <div className="fixed inset-0 z-50">
          <div
            className={`absolute inset-0 bg-black/30 transition-opacity duration-300 ${
              isTaskSidebarVisible ? "opacity-100" : "opacity-0"
            }`}
            onMouseDown={closeTaskSidebar}
          />
          <TaskDetailComponent
            task={selectedTask}
            refetchTasks={handleRefetchTasks}
            refetchSubtasks={handleRefetchSubtasks}
            onClose={closeTaskSidebar}
            onOpenSubtask={handleSetSelectedSubtask}
            onAssignApprovers={handleAssignApprovers}
            onAddUpdate={handleAddTaskUpdate}
            isVisible={isTaskSidebarVisible}
          />
        </div>
      )}

      {selectedSubtask && (
        <div className="fixed inset-0 z-[60]">
          <div
            className={`absolute inset-0 bg-black/30 transition-opacity duration-300 ${
              isSubtaskSidebarVisible ? "opacity-100" : "opacity-0"
            }`}
            onMouseDown={closeSubtaskSidebar}
          />
          <SubtaskDetailComponent
            refetchSubtasks={handleRefetchSubtasks}
            subtask={selectedSubtask}
            taskId={selectedSubtask.task_id}
            onClose={closeSubtaskSidebar}
            isVisible={isSubtaskSidebarVisible}
          />
        </div>
      )}
    </div>
  );
};

export default TaskHome;
